import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const Hero: React.FC = () => {
  return (
    <section className="relative overflow-hidden bg-[#4b227a]">
      {/* Animated Gradient Background */}
      <motion.div 
        className="absolute inset-0 bg-gradient-to-r from-[#4b227a] to-[#5b2d8f] opacity-95"
        animate={{
          background: [
            'linear-gradient(135deg, #4b227a 0%, #5b2d8f 100%)',
            'linear-gradient(135deg, #5b2d8f 0%, #6a3ba4 100%)',
            'linear-gradient(135deg, #6a3ba4 0%, #4b227a 100%)',
          ],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      ></motion.div>

      {/* Animated floating particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              x: [0, Math.random() * 40 - 20, 0],
              opacity: [0.3, 0.7, 0.3],
            }}
            transition={{
              duration: Math.random() * 10 + 5,
              repeat: Infinity,
              delay: Math.random() * 5,
            }}
          />
        ))}
      </div>

      {/* Animated glow orbs */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-400/20 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/15 rounded-full blur-3xl"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
      />

      {/* Animated grid pattern */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(90deg, transparent 95%, rgba(255,255,255,0.1) 100%),
              linear-gradient(0deg, transparent 95%, rgba(255,255,255,0.1) 100%)
            `,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="relative container-custom section-padding">
        <div className="grid lg:grid-cols-2 items-center gap-12">

          {/* LEFT CONTENT */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-white z-10"
          >
            {/* Animated brand name */}
            <motion.h2 
              className="text-7xl font-light mb-4 tracking-tight"
              animate={{
                textShadow: [
                  "0 0 20px rgba(255,255,255,0.3)",
                  "0 0 40px rgba(255,255,255,0.5)",
                  "0 0 20px rgba(255,255,255,0.3)",
                ],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              GetNailedbyAJ
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="uppercase tracking-wide text-lg mb-8 text-purple-200"
            >
              Quality Beauty Treatments
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="text-4xl md:text-5xl font-bold leading-tight mb-6"
            >
              Experience the Best <br />
              Beauty Services
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="text-purple-200 text-lg mb-10 max-w-md"
            >
              at an award winning beauty studio
            </motion.p>

            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 0 30px rgba(255,255,255,0.4)"
              }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center bg-white text-[#4b227a] px-7 py-3 rounded-full font-semibold hover:bg-purple-100 transition-all group"
            >
              Book now
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ 
                  duration: 1.5, 
                  repeat: Infinity,
                  repeatType: "reverse" 
                }}
              >
                <ArrowRight className="ml-2" size={20} />
              </motion.div>
            </motion.button>

            {/* Floating stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1 }}
              className="mt-12 flex flex-wrap gap-8"
            >
              {[
                { value: "500+", label: "Happy Clients" },
                { value: "4.9", label: "Star Rating" },
                { value: "5+", label: "Years Experience" },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-purple-300">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* RIGHT IMAGE SECTION */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="relative h-[520px] lg:h-[650px] rounded-3xl overflow-hidden shadow-xl"
          >
            {/* Animated image container */}
            <motion.div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('/hero.jpg')`
              }}
              animate={{
                scale: [1, 1.05, 1],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            {/* Animated overlay gradient */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-l from-[#4b227a]/40"
              animate={{
                background: [
                  'linear-gradient(90deg, rgba(75,34,122,0.4) 0%, rgba(91,45,143,0.2) 100%)',
                  'linear-gradient(90deg, rgba(75,34,122,0.3) 0%, rgba(106,59,164,0.3) 100%)',
                  'linear-gradient(90deg, rgba(75,34,122,0.4) 0%, rgba(91,45,143,0.2) 100%)',
                ],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            {/* Floating elements on image */}
            <motion.div
              className="absolute top-8 left-8 bg-white/10 backdrop-blur-sm rounded-xl p-3"
              animate={{
                y: [0, -10, 0],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <div className="text-white text-2xl"></div>
            </motion.div>

            <motion.div
              className="absolute bottom-8 right-8 bg-white/10 backdrop-blur-sm rounded-xl p-4"
              animate={{
                y: [0, 10, 0],
                rotate: [0, -5, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.5,
              }}
            >
              <div className="text-white text-2xl"></div>
            </motion.div>

            {/* Glow effect around image */}
            <div className="absolute -inset-4 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-purple-500/20 rounded-3xl blur-xl -z-10" />
          </motion.div>
        </div>
      </div>

     

      {/* Animated wave at bottom */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/10 to-transparent"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 1200 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0v46.29c47.79 22.2 103.59 32.17 158 28 70.36-5.37 136.33-33.31 206.8-37.5 73.84-4.36 147.54 16.88 218.2 35.26 69.27 18 138.3 24.88 209.4 13.08 36.15-6 69.85-17.84 104.45-29.34C989.49 25 1113-14.29 1200 52.47V0z' fill='%23ffffff' opacity='0.1'/%3E%3C/svg%3E")`,
          backgroundSize: '1200px 100%',
        }}
        animate={{
          backgroundPositionX: [0, '1200px'],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear",
        }}
      />
    </section>
  );
};

export default Hero;